import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../PageHeader/PageHeader';
import { HitApi } from '../../Api/ApiHit';
import { deleteUnit, searchUnit, updateUnit, searchCashBalance, addCashBalance } from '../../constant/Constant';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../redux/Actions/ApiAction';
import { setPagination } from '../../redux/Actions/TableAction';
import DeleteButton from '../../components/Delete/DeleteButton';
import EditButton from '../../components/Edit/EditButton';
import toast from 'react-hot-toast';
import TableSearch from '../../components/TableSearch/TableSearch';
import { hasPermission } from '../../utils/permissionUtils';
import DeleteConfirmationModal from '../../components/CustomModal/DeleteConfirmationModal';
import PrinterConfiguration from './PrinterConfiguration';
import CashDrawerControl from './CashDrawerControl';
import { printerProxyService } from '../../utils/printerProxyService';

function UnitMaster() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);
    
    // Get current user for cash balance creation
    const authUser = useSelector((state) => state.auth?.user);
    const authReducerUser = useSelector((state) => state.AuthReducer?.user);
    const currentUser = authUser || authReducerUser || JSON.parse(localStorage.getItem('user') || '{}');

    // Search state variables
    const [optionSelect, setOptionSelect] = useState('');
    const [valueSearched, setValueSearched] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [unitToDelete, setUnitToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // QR Code modal state
    const [showQrModal, setShowQrModal] = useState(false);
    const [qrUnit, setQrUnit] = useState(null);

    // Configuration modal state
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [unitToConfig, setUnitToConfig] = useState(null);
    const [isSavingConfig, setIsSavingConfig] = useState(false);
    
    // Printer and drawer state
    const [printerEnabled, setPrinterEnabled] = useState(false);
    const [cashDrawerEnabled, setCashDrawerEnabled] = useState(false);
    const [selectedPrinter, setSelectedPrinter] = useState(null);
    
    // Test connection state
    const [isTestingPrinter, setIsTestingPrinter] = useState(false);
    const [isTestingDrawer, setIsTestingDrawer] = useState(false);
    
    // Cash balance state
    const [cashBalance, setCashBalance] = useState(null);
    const [isLoadingCashBalance, setIsLoadingCashBalance] = useState(false);
    const [isCreatingCashBalance, setIsCreatingCashBalance] = useState(false);
    
    // Enhanced printer state for comprehensive functionality
    const [ePosDev, setEPosDev] = useState(null);
    const [printer, setPrinter] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionMethod, setConnectionMethod] = useState('epos-print');
    const [lastConnectionTest, setLastConnectionTest] = useState(null);
    const [availablePrinters, setAvailablePrinters] = useState([]);
    const [isDetecting, setIsDetecting] = useState(false);
    const [detectionProgress, setDetectionProgress] = useState(0);
    
    // SDK and API support detection
    const [isSDKAvailable, setIsSDKAvailable] = useState(false);
    const [sdkInitialized, setSdkInitialized] = useState(false);
    
    // Print quality settings
    const [printQualitySettings, setPrintQualitySettings] = useState({
        density: 'high',        // 'low', 'medium', 'high', 'ultra'
        speed: 'medium',        // 'slow', 'medium', 'fast'
        smoothing: true,        // Enable character smoothing
        doubleStrike: false,    // Enable double strike for darker text
        enhancedGraphics: true, // Enhanced graphics mode
        paperSaving: false,     // Reduce line spacing for paper saving
        fontEnhancement: true   // Better font rendering
    });
    
    // Refs for cleanup
    const abortControllerRef = useRef(null);
    const detectionTimeoutRef = useRef(null);

    // Permission checks using hasPermission utility
    const canEditUnit = hasPermission('unit', 'write');
    const canReadUnit = hasPermission('unit', 'read');
    const canDeleteUnit = hasPermission('unit', 'delete');
    const canAddUnit = hasPermission('unit', 'write');

    console.log({ canEditUnit, canReadUnit, canDeleteUnit, canAddUnit });

    // Initialize ePOS SDK on component mount
    useEffect(() => {
        initializeEposSDK();
        return () => {
            // Cleanup on component unmount
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (detectionTimeoutRef.current) {
                clearTimeout(detectionTimeoutRef.current);
            }
            disconnectFromPrinter();
        };
    }, []);

    useEffect(() => {
        if (canReadUnit) {
            fetchUnit();
        }
    }, [TableDataReducer.pagination.page, TableDataReducer.pagination.limit, canReadUnit]);

    const fetchUnit = (searchKey, searchValue) => {
        if (!canReadUnit) {
            toast.error('You do not have permission to view units');
            return;
        }

        setIsSearching(true);

        const searchObj = {};

        // Only add search key/value if both are provided
        if (searchKey && searchValue) {
            searchObj[searchKey] = searchValue;
        }

        const json = {
            page: TableDataReducer.pagination.page,
            limit: TableDataReducer.pagination.limit,
            search: searchObj
        };

        HitApi(json, searchUnit).then((res) => {
            console.log("res______", res);

            if (res?.statusCode == 200) {
                const unitData = Array.isArray(res?.data.docs) ? res?.data.docs : [];
                dispatch(setApiJson(unitData));

                const paginationData = {
                    total: res?.data?.totalDocs || 0,
                    page: TableDataReducer.pagination.page,
                    limit: TableDataReducer.pagination.limit
                };

                dispatch(setPagination(paginationData));
            } else {
                dispatch(setApiJson([]));
                toast.error('Failed to fetch units. Please try again.');
            }
        }).catch(error => {
            dispatch(setApiJson([]));
            toast.error('Error fetching units. Please try again.');
        }).finally(() => {
            setIsSearching(false);
        });
    };

    // Search functionality
    const handleSearch = () => {
        if (!canReadUnit) {
            toast.error('You do not have permission to search units');
            return;
        }

        if (!optionSelect) {
            toast.error('Please select a search option');
            return;
        }
        if (!valueSearched || valueSearched.trim() === '') {
            toast.error('Please enter a search value');
            return;
        }
        fetchUnit(optionSelect, valueSearched.trim());
    };

    // Clear search functionality
    const handleClearSearch = () => {
        if (!canReadUnit) {
            toast.error('You do not have permission to access units');
            return;
        }

        setOptionSelect('');
        setValueSearched('');
        fetchUnit();
    };

    // Search options based on table keys
    const options = [
        { label: 'Unit Name', value: 'unitName' },
        { label: 'Unit Code', value: 'unitCode' },
        { label: 'Address', value: 'address' },
        { label: 'Status', value: 'status' },
    ];

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    const handleEdit = (row) => {
        if (!canEditUnit) {
            toast.error('You do not have permission to edit units');
            return;
        }
        navigate(`/unit/edit/${row._id}`);
    };

    // Updated handleDelete to show modal instead of window.confirm
    const handleDelete = (row) => {
        if (!canDeleteUnit) {
            toast.error('You do not have permission to delete units');
            return;
        }

        setUnitToDelete(row);
        setShowDeleteModal(true);
    };

    // Handle actual deletion after confirmation
    const confirmDelete = () => {
        if (!unitToDelete) return;

        setIsDeleting(true);

        const json = {
            _id: unitToDelete._id
        };

        HitApi(json, deleteUnit).then((res) => {
            if (res?.statusCode === 200) {
                toast.success('Unit deleted successfully!');
                fetchUnit();
                setShowDeleteModal(false);
                setUnitToDelete(null);
            } else {
                toast.error('Failed to delete unit. Please try again.');
            }
        }).catch(error => {
            toast.error('Error occurred while deleting unit. Please try again.');
        }).finally(() => {
            setIsDeleting(false);
        });
    };

    // Handle modal close
    const handleCloseModal = () => {
        if (!isDeleting) {
            setShowDeleteModal(false);
            setUnitToDelete(null);
        }
    };

    // Handle configuration
    const handleConfig = (row) => {
        if (!canEditUnit) {
            toast.error('You do not have permission to configure units');
            return;
        }
        setUnitToConfig(row);
        
        // Load existing configuration
        setPrinterEnabled(row.printerEnabled || (row.printerSetting && row.printerSetting.length > 0));
        const drawerEnabled = row.cashDrawerEnabled || (row.cashDrawerSettings && row.cashDrawerSettings.length > 0);
        setCashDrawerEnabled(drawerEnabled);
        
        // Load existing cash balance if drawer is enabled
        if (drawerEnabled) {
            searchCashBalanceForUnit(row._id);
        } else {
            setCashBalance(null);
        }
        
        // Load existing printer settings if available (handle both printerSettings object and printerSetting array)
        if (row.printerSettings) {
            // New structure (object)
            setSelectedPrinter({
                ip: row.printerSettings.ip,
                port: row.printerSettings.port,
                method: row.printerSettings.method,
                success: true
            });
        } else if (row.printerSetting && row.printerSetting.length > 0) {
            // Existing structure (array)
            const printer = row.printerSetting[0];
            setSelectedPrinter({
                ip: printer.ip,
                port: printer.port,
                method: printer.method || 'epos-print',
                success: true
            });
        } else {
            setSelectedPrinter(null);
        }
        
        setShowConfigModal(true);
    };

    // Handle configuration modal close
    const handleCloseConfigModal = () => {
        if (!isSavingConfig) {
            setShowConfigModal(false);
            setUnitToConfig(null);
            // Reset printer/drawer states when closing
            setPrinterEnabled(false);
            setCashDrawerEnabled(false);
            setSelectedPrinter(null);
            // Reset test states
            setIsTestingPrinter(false);
            setIsTestingDrawer(false);
            // Reset cash balance states
            setCashBalance(null);
            setIsLoadingCashBalance(false);
            setIsCreatingCashBalance(false);
        }
    };

    // Initialize ePOS SDK
    const initializeEposSDK = useCallback(() => {
        try {
            if (typeof window !== 'undefined' && window.ePosDev) {
                console.log('ePOS SDK is available');
                setIsSDKAvailable(true);
                setSdkInitialized(true);
            } else {
                console.log('ePOS SDK not available, using XML API fallback');
                setIsSDKAvailable(false);
                setSdkInitialized(false);
            }
        } catch (error) {
            console.error('Error initializing ePOS SDK:', error);
            setIsSDKAvailable(false);
            setSdkInitialized(false);
        }
    }, []);

    // Enhanced printer connection with dual SDK support
    const connectToPrinter = useCallback(async (ip, port = 8008, deviceId = 'local_printer') => {
        try {
            setIsConnected(false);
            
            if (isSDKAvailable && window.ePosDev) {
                console.log(`Connecting to printer via ePOS SDK: ${ip}:${port}`);
                
                return new Promise((resolve, reject) => {
                    const ePosDev = new window.ePosDev();
                    
                    ePosDev.onconnect = (deviceObj) => {
                        console.log('ePOS SDK connected successfully');
                        const printer = deviceObj.createDevice(
                            'local_printer',
                            window.ePosDev.DEVICE_TYPE_PRINTER,
                            { 
                                'crypto': false, 
                                'buffer': false,
                                'timeout': 60000,  // 60 second timeout
                                'interval': 3000   // 3 second status check interval
                            }
                        );
                        
                        setEPosDev(ePosDev);
                        setPrinter(printer);
                        setIsConnected(true);
                        setConnectionMethod('epos-sdk');
                        
                        resolve({ ePosDev, printer, method: 'epos-sdk' });
                    };
                    
                    ePosDev.onerror = (error) => {
                        console.error('ePOS SDK connection failed:', error);
                        setIsConnected(false);
                        reject(new Error(`ePOS SDK connection failed: ${error.status}`));
                    };
                    
                    // Connect to printer
                    ePosDev.connect(ip, port, (result) => {
                        if (result !== 'OK') {
                            reject(new Error(`Connection failed: ${result}`));
                        }
                    });
                });
            } else {
                // Fallback to XML API
                console.log(`Testing connection via XML API: ${ip}:${port}`);
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 5000);
                
                const testUrl = port === 8008 
                    ? `http://${ip}:${port}/epos/capability.xml`
                    : `http://${ip}/epos/capability.xml`;
                
                await fetch(testUrl, {
                    method: 'GET',
                    mode: 'no-cors',
                    signal: controller.signal,
                    headers: { 'Accept': 'application/xml, text/xml, */*' }
                });
                
                clearTimeout(timeoutId);
                setIsConnected(true);
                setConnectionMethod('epos-print');
                
                return { method: 'epos-print', ip, port };
            }
        } catch (error) {
            setIsConnected(false);
            throw error;
        }
    }, [isSDKAvailable]);

    // Disconnect from printer
    const disconnectFromPrinter = useCallback(() => {
        try {
            if (ePosDev) {
                ePosDev.disconnect();
                setEPosDev(null);
            }
            setPrinter(null);
            setIsConnected(false);
            setConnectionMethod('epos-print');
            console.log('Disconnected from printer');
        } catch (error) {
            console.error('Error disconnecting from printer:', error);
        }
    }, [ePosDev]);

    // Advanced printer auto-detection
    const detectPrinters = useCallback(async (ipRanges = ['192.168.1.', '192.168.0.'], options = {}) => {
        const {
            startRange = 1,
            endRange = 254,
            timeout = 3000,
            batchSize = 10,
            excludeRanges = ['10.0.0.']
        } = options;
        
        setIsDetecting(true);
        setDetectionProgress(0);
        setAvailablePrinters([]);
        
        const foundPrinters = [];
        
        try {
            for (const ipRange of ipRanges) {
                // Skip excluded ranges
                if (excludeRanges.some(excluded => ipRange.startsWith(excluded))) {
                    console.log(`Skipping excluded IP range: ${ipRange}`);
                    continue;
                }
                
                const totalIps = endRange - startRange + 1;
                let processed = 0;
                
                // Process IPs in batches
                for (let i = startRange; i <= endRange; i += batchSize) {
                    const batch = [];
                    const batchEnd = Math.min(i + batchSize - 1, endRange);
                    
                    for (let j = i; j <= batchEnd; j++) {
                        const ip = `${ipRange}${j}`;
                        batch.push(testPrinterAtIP(ip, timeout));
                    }
                    
                    try {
                        const results = await Promise.allSettled(batch);
                        
                        results.forEach((result, index) => {
                            if (result.status === 'fulfilled' && result.value) {
                                foundPrinters.push(result.value);
                                setAvailablePrinters(prev => [...prev, result.value]);
                            }
                        });
                        
                        processed += batch.length;
                        setDetectionProgress(Math.round((processed / totalIps) * 100));
                        
                        // Small delay to prevent overwhelming the network
                        await new Promise(resolve => setTimeout(resolve, 100));
                        
                    } catch (error) {
                        console.error(`Batch detection error for range ${ipRange}:`, error);
                    }
                }
            }
            
            console.log(`Detection complete. Found ${foundPrinters.length} printers:`, foundPrinters);
            return foundPrinters;
            
        } catch (error) {
            console.error('Printer detection failed:', error);
            throw error;
        } finally {
            setIsDetecting(false);
            setDetectionProgress(100);
        }
    }, []);

    // Test printer at specific IP
    const testPrinterAtIP = useCallback(async (ip, timeout = 3000) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        const isProduction = window.location.protocol === 'https:';
        
        try {
            if (isProduction) {
                // Use proxy service in production to avoid mixed content errors
                clearTimeout(timeoutId);
                const result = await printerProxyService.testSinglePrinter(ip, timeout);
                
                if (result) {
                    return {
                        ...result,
                        success: true,
                        timestamp: new Date().toISOString()
                    };
                }
                
                return null;
            } else {
                // Development environment: Direct HTTP requests
                const ports = [80, 8008];
                
                for (const port of ports) {
                    try {
                        const testUrl = port === 8008 
                            ? `http://${ip}:${port}/epos/capability.xml`
                            : `http://${ip}/epos/capability.xml`;
                        
                        await fetch(testUrl, {
                            method: 'GET',
                            mode: 'no-cors',
                            signal: controller.signal,
                            headers: { 'Accept': 'application/xml, text/xml, */*' }
                        });
                        
                        clearTimeout(timeoutId);
                        return {
                            ip,
                            port: 8008, // Store as 8008 for configuration
                            testPort: port, // Keep track of test port
                            method: 'epos-print',
                            name: `Printer-${ip}`,
                            success: true,
                            timestamp: new Date().toISOString(),
                            environment: 'development'
                        };
                    } catch (portError) {
                        // Continue to next port
                        continue;
                    }
                }
            }
            
            return null;
        } catch (error) {
            clearTimeout(timeoutId);
            return null;
        }
    }, []);

    // Production-ready printer detection using backend proxy
    const detectPrintersViaProxy = useCallback(async (ipRanges = ['192.168.1.', '192.168.0.'], options = {}) => {
        const isProduction = window.location.protocol === 'https:';
        
        if (!isProduction) {
            // Use existing detection for development
            return detectPrinters(ipRanges, options);
        }

        setIsDetecting(true);
        setDetectionProgress(0);
        setAvailablePrinters([]);

        try {
            // Check if proxy is available
            const proxyAvailable = await printerProxyService.checkProxyHealth();
            
            if (!proxyAvailable) {
                toast.error('Backend proxy is not available. Please ensure your backend server is running.');
                throw new Error('Proxy service unavailable');
            }

            toast.loading('Using backend proxy for printer detection...', {
                id: 'proxy-detection',
                duration: 3000
            });

            // Use proxy service to scan IP ranges
            const foundPrinters = await printerProxyService.scanIPRanges(ipRanges, {
                ...options,
                onProgress: (progress) => {
                    setDetectionProgress(progress.percentage);
                }
            });

            // Update state with found printers
            setAvailablePrinters(foundPrinters);

            if (foundPrinters.length > 0) {
                toast.success(`Found ${foundPrinters.length} printer(s) via backend proxy`, {
                    id: 'proxy-detection'
                });
            } else {
                toast('No printers found. You can manually add printer IP addresses.', {
                    id: 'proxy-detection',
                    icon: 'ℹ️',
                    duration: 5000
                });
            }

            return foundPrinters;

        } catch (error) {
            console.error('Proxy printer detection failed:', error);
            toast.error('Printer detection failed. Falling back to manual configuration.', {
                id: 'proxy-detection'
            });

            // Return empty array to indicate no printers found
            return [];
        } finally {
            setIsDetecting(false);
            setDetectionProgress(100);
        }
    }, [detectPrinters]);

    // Enhanced bill printing with dual SDK support and quality settings
    const printBillWithEposSDK = useCallback(async (billData, options = {}) => {
        if (!printer || !isConnected) {
            throw new Error('Printer not connected');
        }
        
        const { openDrawer = false, includeCut = true, qualitySettings = printQualitySettings } = options;
        
        return new Promise((resolve, reject) => {
            try {
                // Apply print quality settings
                if (qualitySettings.density === 'ultra') {
                    printer.addCommand([0x1D, 0x7C, 0x00]); // Set high density
                } else if (qualitySettings.density === 'high') {
                    printer.addCommand([0x1B, 0x7A, 0x01]); // High quality mode
                }
                
                // Set print speed
                if (qualitySettings.speed === 'slow') {
                    printer.addCommand([0x1B, 0x73, 0x00]); // Slow speed for better quality
                } else if (qualitySettings.speed === 'medium') {
                    printer.addCommand([0x1B, 0x73, 0x01]); // Medium speed
                }
                
                // Enable smoothing
                if (qualitySettings.smoothing) {
                    printer.addCommand([0x1B, 0x74, 0x01]); // Character smoothing on
                }
                
                // Enable double strike for darker text
                if (qualitySettings.doubleStrike) {
                    printer.addTextStyle(false, false, true, printer.COLOR_1); // Double strike
                }
                
                // Enhanced graphics mode
                if (qualitySettings.enhancedGraphics) {
                    printer.addCommand([0x1B, 0x2A, 0x21]); // Enhanced graphics
                }
                
                // Build receipt content with enhanced formatting
                printer.addTextAlign(printer.ALIGN_CENTER);
                
                // Business header with enhanced styling
                if (qualitySettings.fontEnhancement) {
                    printer.addTextStyle(false, false, true, printer.COLOR_1); // Bold
                }
                printer.addTextSize(2, 2);
                printer.addText(`${billData.business?.name || 'RECEIPT'}\n`);
                
                // Reset to normal style
                printer.addTextStyle(false, false, false, printer.COLOR_1);
                printer.addTextSize(1, 1);
                
                // Business details
                printer.addText(`${billData.business?.address || ''}\n`);
                printer.addText(`${billData.business?.phone || ''}\n`);
                
                // Add separator line with enhanced characters
                printer.addText('\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\n');
                
                if (!qualitySettings.paperSaving) {
                    printer.addFeedLine(1);
                }
                
                // Transaction details
                printer.addTextAlign(printer.ALIGN_LEFT);
                if (qualitySettings.fontEnhancement) {
                    printer.addTextStyle(false, false, true, printer.COLOR_1); // Bold for headers
                }
                printer.addText(`Bill #: ${billData.billNumber || 'N/A'}\n`);
                printer.addText(`Date: ${new Date().toLocaleString()}\n`);
                printer.addText(`Customer: ${billData.client?.name || 'Walk-in'}\n`);
                
                // Reset style
                printer.addTextStyle(false, false, false, printer.COLOR_1);
                printer.addText('\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\n');
                
                // Add services with enhanced formatting
                if (billData.services && billData.services.length > 0) {
                    billData.services.forEach((service, index) => {
                        // Service name with enhanced styling
                        if (qualitySettings.fontEnhancement) {
                            printer.addTextStyle(false, false, true, printer.COLOR_1);
                        }
                        printer.addText(`${index + 1}. ${service.name || 'Service'}\n`);
                        
                        // Reset style for details
                        printer.addTextStyle(false, false, false, printer.COLOR_1);
                        
                        // Quantity and price alignment
                        const qty = service.quantity || 1;
                        const price = service.pricing?.finalPrice || 0;
                        const total = qty * price;
                        
                        printer.addText(`   Qty: ${qty} x $${price.toFixed(2)} = $${total.toFixed(2)}\n`);
                        
                        if (service.staff?.name) {
                            printer.addText(`   Staff: ${service.staff.name}\n`);
                        }
                        
                        if (!qualitySettings.paperSaving && index < billData.services.length - 1) {
                            printer.addFeedLine(1);
                        }
                    });
                }
                
                printer.addText('\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\n');
                
                // Total with enhanced styling
                printer.addTextAlign(printer.ALIGN_RIGHT);
                if (qualitySettings.fontEnhancement) {
                    printer.addTextStyle(false, false, true, printer.COLOR_1);
                    printer.addTextSize(1, 2);
                }
                printer.addText(`TOTAL: $${billData.calculations?.totals?.grandTotal?.toFixed(2) || '0.00'}\n`);
                
                // Reset style
                printer.addTextStyle(false, false, false, printer.COLOR_1);
                printer.addTextSize(1, 1);
                printer.addTextAlign(printer.ALIGN_LEFT);
                
                // Payment details
                if (billData.payment?.activePaymentMethods) {
                    if (!qualitySettings.paperSaving) {
                        printer.addFeedLine(1);
                    }
                    printer.addText('Payment Details:\n');
                    billData.payment.activePaymentMethods.forEach(payment => {
                        printer.addText(`  ${payment.method}: $${payment.amount?.toFixed(2) || '0.00'}\n`);
                    });
                }
                
                // Footer with enhanced styling
                if (!qualitySettings.paperSaving) {
                    printer.addFeedLine(2);
                } else {
                    printer.addFeedLine(1);
                }
                
                printer.addTextAlign(printer.ALIGN_CENTER);
                printer.addText('\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\n');
                printer.addText('Thank you for your business!\n');
                if (!qualitySettings.paperSaving) {
                    printer.addFeedLine(1);
                }
                printer.addText('\u2022 Powered by Elevate Panel \u2022\n');
                
                if (!qualitySettings.paperSaving) {
                    printer.addFeedLine(2);
                } else {
                    printer.addFeedLine(1);
                }
                
                if (includeCut) {
                    printer.addCut(printer.CUT_FEED);
                }
                
                if (openDrawer) {
                    printer.addPulse(printer.DRAWER_1, printer.PULSE_100);
                }
                
                printer.send();
                
                printer.onreceive = (response) => {
                    if (response.success) {
                        console.log('Enhanced quality bill printed successfully via ePOS SDK');
                        resolve(response);
                    } else {
                        reject(new Error(`Print failed: ${response.code}`));
                    }
                };
                
                printer.onerror = (error) => {
                    reject(new Error(`Print error: ${error.status}`));
                };
                
            } catch (error) {
                reject(error);
            }
        });
    }, [printer, isConnected, printQualitySettings]);

    // Enhanced cash drawer control
    const openCashDrawerOnly = useCallback(async (ip, port = 8008, options = {}) => {
        const { useSDK = isSDKAvailable, timeout = 5000 } = options;
        
        try {
            if (useSDK && printer && isConnected) {
                // Use ePOS SDK
                return new Promise((resolve, reject) => {
                    printer.addPulse(printer.DRAWER_1, printer.PULSE_100);
                    printer.send();
                    
                    printer.onreceive = (response) => {
                        if (response.success) {
                            console.log('Cash drawer opened via ePOS SDK');
                            resolve(response);
                        } else {
                            reject(new Error(`Drawer open failed: ${response.code}`));
                        }
                    };
                    
                    printer.onerror = (error) => {
                        reject(new Error(`Drawer error: ${error.status}`));
                    };
                });
            } else {
                // Use proxy service for HTTPS or XML API for HTTP
                const isProduction = window.location.protocol === 'https:';
                
                if (isProduction) {
                    console.log('Using proxy service for cash drawer in production...');
                    const result = await printerProxyService.openDrawer(ip, port, 'epos-print');
                    
                    if (result.success) {
                        console.log('Cash drawer opened via proxy service');
                        return result;
                    } else {
                        throw new Error(result.error || 'Failed to open drawer via proxy');
                    }
                } else {
                    // Development: Direct HTTP connection
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), timeout);
                    
                    const drawerXml = `<?xml version="1.0" encoding="utf-8"?>
<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">
    <pulse drawer="drawer_1" time="pulse_100"/>
</epos-print>`;
                    
                    const printUrl = port === 8008 
                        ? `http://${ip}:${port}/epos/print`
                        : `http://${ip}/epos/print`;
                    
                    const response = await fetch(printUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'text/xml; charset=utf-8',
                            'SOAPAction': ''
                        },
                        body: drawerXml,
                        mode: 'no-cors',
                        signal: controller.signal
                    });
                    
                    clearTimeout(timeoutId);
                    console.log('Cash drawer opened via XML API');
                    return response;
                }
            }
        } catch (error) {
            console.error('Failed to open cash drawer:', error);
            throw error;
        }
    }, [printer, isConnected, isSDKAvailable]);

    // Handle printer data change
    const handlePrinterDataChange = (printerData) => {
        setSelectedPrinter(printerData.selectedPrinter);
        if (!printerData.printerEnabled) {
            setCashDrawerEnabled(false);
            setCashBalance(null);
        }
    };

    // Handle cash drawer toggle with cash balance logic
    const handleCashDrawerToggle = async (enabled) => {
        if (!unitToConfig) {
            toast.error('No unit selected');
            return;
        }

        setCashDrawerEnabled(enabled);

        if (enabled) {
            // When enabling cash drawer, search for existing cash balance or create new one
            toast.loading('Checking cash balance...', { id: 'cash-balance-check' });
            
            const existingBalance = await searchCashBalanceForUnit(unitToConfig._id);
            
            if (!existingBalance) {
                // No existing balance found, create new one
                toast.loading('Creating new cash balance...', { id: 'cash-balance-check' });
                await createCashBalanceForUnit(unitToConfig._id);
            }
            
            toast.dismiss('cash-balance-check');
        } else {
            // When disabling cash drawer, clear cash balance from state
            setCashBalance(null);
        }
    };

    // Search for existing cash balance for the unit
    const searchCashBalanceForUnit = async (unitId) => {
        setIsLoadingCashBalance(true);
        try {
            const json = {
                page: 1,
                limit: 10,
                search: {
                    unitId: unitId
                }
            };

            const response = await HitApi(json, searchCashBalance);
            
            if (response?.statusCode === 200 && response?.data?.docs?.length > 0) {
                // Cash balance exists
                const existingBalance = response.data.docs[0];
                setCashBalance(existingBalance);
                toast.success(`Cash balance found: $${existingBalance.balance || 0}`);
                return existingBalance;
            } else {
                // No cash balance found, create new one
                setCashBalance(null);
                return null;
            }
        } catch (error) {
            console.error('Error searching cash balance:', error);
            toast.error('Error searching cash balance');
            setCashBalance(null);
            return null;
        } finally {
            setIsLoadingCashBalance(false);
        }
    };

    // Create new cash balance for the unit
    const createCashBalanceForUnit = async (unitId) => {
        setIsCreatingCashBalance(true);
        try {
            const json = {
                note500: 0,
                note200: 0,
                note100: 0,
                note50: 0,
                note20: 0,
                note10: 0,
                coin10: 0,
                coin5: 0,
                coin2: 0,
                coin1: 0,
                totalAmount: 0,
                unitId: unitId,
                balanceType: 'Opening Balance',
                balanceDate: new Date().toISOString(),
                recordedBy: currentUser?._id || currentUser?.id || '000000000000000000000000', // fallback ID
                notes: `Initial cash balance for unit: ${unitToConfig?.unitName || 'Unknown Unit'}`
            };

            console.log('Creating cash balance with payload:', json);
            const response = await HitApi(json, addCashBalance);
            
            if (response?.statusCode === 201 || response?.statusCode === 200) {
                // Cash balance created successfully
                const newBalance = response.data;
                setCashBalance(newBalance);
                toast.success('Cash balance created successfully with ₹0 initial balance');
                return newBalance;
            } else {
                toast.error(response?.message || 'Failed to create cash balance');
                return null;
            }
        } catch (error) {
            console.error('Error creating cash balance:', error);
            toast.error('Error creating cash balance');
            return null;
        } finally {
            setIsCreatingCashBalance(false);
        }
    };

    // Enhanced printer connection test with dual SDK support
    const testPrinterConnection = async () => {
        if (!unitToConfig?.printerSetting || unitToConfig.printerSetting.length === 0) {
            toast.error('No printer configuration found to test');
            return;
        }

        setIsTestingPrinter(true);
        const printerConfig = unitToConfig.printerSetting[0];
        setLastConnectionTest(new Date());

        try {
            console.log(`Testing printer connection to ${printerConfig.ip}:${printerConfig.port}`);
            
            // Try connecting with enhanced method
            const connectionResult = await connectToPrinter(printerConfig.ip, printerConfig.port);
            
            if (connectionResult.method === 'epos-sdk' && printer) {
                // Test print using ePOS SDK
                console.log('Testing print via ePOS SDK');
                
                const testBillData = {
                    billNumber: 'TEST-' + Date.now(),
                    business: {
                        name: 'Unit Configuration Test',
                        address: unitToConfig?.unitName || 'Test Unit',
                        phone: 'Configuration Test'
                    },
                    services: [{
                        name: 'Printer Test',
                        quantity: 1,
                        pricing: { finalPrice: 0.00 },
                        staff: { name: 'System Test' }
                    }],
                    calculations: {
                        totals: { grandTotal: 0.00 }
                    },
                    payment: {
                        activePaymentMethods: [{ method: 'Test', amount: 0.00 }],
                        paymentStatus: 'Test Mode'
                    }
                };
                
                await printBillWithEposSDK(testBillData, { openDrawer: false, includeCut: true });
                toast.success(`✅ Printer test successful via ePOS SDK! Connected to ${printerConfig.ip}:${printerConfig.port}`);
                
            } else {
                // Test print using proxy service for production or XML API for development
                console.log('Testing print via XML API/Proxy');
                
                const isProduction = window.location.protocol === 'https:';
                
                // Enhanced test print with quality settings and comprehensive information
                const testXml = `<?xml version="1.0" encoding="utf-8"?>
<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">
    <!-- Quality Settings -->
    <command>1B 7A 01</command> <!-- High quality mode -->
    <command>1B 73 01</command> <!-- Medium speed for quality -->
    <command>1B 74 01</command> <!-- Smoothing on -->
    
    <!-- Header -->
    <text align="center" width="2" height="2" em="true">PRINTER TEST</text>
    <feed line="1"/>
    <text align="center" em="true">Configuration Test Print</text>
    <feed line="1"/>
    <text align="center">${new Date().toLocaleString()}</text>
    <feed line="1"/>
    
    <!-- Enhanced separator -->
    <text>\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550</text>
    <feed line="1"/>
    
    <!-- System Information -->
    <text em="true">SYSTEM INFORMATION:</text>
    <feed line="1"/>
    <text>Unit: ${unitToConfig?.unitName || 'N/A'}</text>
    <feed line="1"/>
    <text>IP Address: ${printerConfig.ip}</text>
    <feed line="1"/>
    <text>Port: ${printerConfig.port} (${connectionResult.method || 'ePOS-Print'})</text>
    <feed line="1"/>
    <text>Method: ${printerConfig.method || 'ePOS-Print'}</text>
    <feed line="1"/>
    <text>SDK Available: ${isSDKAvailable ? 'Yes' : 'No'}</text>
    <feed line="1"/>
    <text>Environment: ${isProduction ? 'Production (Proxy)' : 'Development (Direct)'}</text>
    <feed line="1"/>
    <text>Quality Mode: Enhanced</text>
    <feed line="1"/>
    <text>Status: \u2705 Connection Successful</text>
    <feed line="1"/>
    
    <!-- Quality Test Patterns -->
    <text>\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500</text>
    <feed line="1"/>
    <text em="true">QUALITY TEST PATTERNS:</text>
    <feed line="1"/>
    <text>Normal: ABCDEFGHIJKLMNOPQRSTUVWXYZ</text>
    <feed line="1"/>
    <text em="true">Bold: ABCDEFGHIJKLMNOPQRSTUVWXYZ</text>
    <feed line="1"/>
    <text>Numbers: 0123456789 ${'\u2022'.repeat(10)}</text>
    <feed line="1"/>
    <text>Special: \u2550\u2551\u2552\u2553\u2554\u2555\u2556\u2557\u2558\u2559\u255A\u255B\u255C\u255D\u255E\u255F</text>
    <feed line="1"/>
    
    <text>\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500</text>
    <feed line="1"/>
    <text align="center">Test completed at:</text>
    <feed line="1"/>
    <text align="center">${new Date().toLocaleString()}</text>
    <feed line="2"/>
    <text align="center" width="1" height="2">\u2705 ALL SYSTEMS OPERATIONAL!</text>
    <feed line="1"/>
    <text align="center">\u2022 Enhanced Quality Mode Active \u2022</text>
    <feed line="1"/>
    <text align="center">\u2022 Powered by Elevate Panel \u2022</text>
    <feed line="3"/>
    <cut type="feed"/>
</epos-print>`;

                if (isProduction) {
                    console.log('Using proxy service for print test...');
                    const result = await printerProxyService.print(
                        printerConfig.ip, 
                        printerConfig.port, 
                        testXml, 
                        printerConfig.method || 'epos-print'
                    );
                    
                    if (result.success) {
                        console.log('Print test successful via proxy service');
                        toast.success(`✅ Printer test successful via Proxy Service! Receipt printed on ${printerConfig.ip}:${printerConfig.port}`);
                    } else {
                        throw new Error(result.error || 'Print test failed via proxy');
                    }
                } else {
                    // Development: Direct HTTP connection
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 8000);
                    
                    const printUrl = printerConfig.port === 8008 
                        ? `http://${printerConfig.ip}:${printerConfig.port}/epos/print`
                        : `http://${printerConfig.ip}/epos/print`;
                    
                    await fetch(printUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'text/xml; charset=utf-8',
                            'SOAPAction': ''
                        },
                        body: testXml,
                        mode: 'no-cors',
                        signal: controller.signal
                    });
                    
                    clearTimeout(timeoutId);
                    console.log('XML API test successful for', `${printerConfig.ip}:${printerConfig.port}`);
                    toast.success(`✅ Printer test successful via XML API! Receipt printed on ${printerConfig.ip}:${printerConfig.port}`);
                }
            }
            
        } catch (error) {
            console.error('Enhanced printer test failed:', error);
            if (error.name === 'AbortError') {
                toast.error('⏱️ Connection test timed out - printer may be offline or unreachable');
            } else if (error.name === 'TypeError') {
                toast.error(`🌐 Network error - Check if printer ${printerConfig.ip} is accessible on your network`);
            } else {
                toast.error(`❌ Printer test failed: ${error.message}. Check ePOS-Print settings and SDK availability.`);
            }
        } finally {
            setIsTestingPrinter(false);
            // Don't disconnect immediately, keep connection for potential cash drawer test
        }
    };

    // Enhanced cash drawer test with dual SDK support
    const testDrawerConnection = async () => {
        if (!unitToConfig?.cashDrawerSettings || unitToConfig.cashDrawerSettings.length === 0) {
            toast.error('No cash drawer configuration found to test');
            return;
        }

        setIsTestingDrawer(true);
        const drawerConfig = unitToConfig.cashDrawerSettings[0];

        try {
            console.log(`Testing cash drawer connection to ${drawerConfig.ip}:${drawerConfig.port}`);
            
            // First ensure we're connected to the printer
            if (!isConnected) {
                console.log('Connecting to printer for drawer test...');
                await connectToPrinter(drawerConfig.ip, drawerConfig.port);
            }
            
            if (isConnected && printer && connectionMethod === 'epos-sdk') {
                // Use ePOS SDK for drawer control
                console.log('Testing cash drawer via ePOS SDK');
                
                // Print test receipt first, then open drawer
                printer.addTextAlign(printer.ALIGN_CENTER);
                printer.addTextSize(2, 2);
                printer.addText('DRAWER TEST\n');
                printer.addTextSize(1, 1);
                printer.addText('Cash Drawer Test\n');
                printer.addText(`${new Date().toLocaleString()}\n`);
                printer.addFeedLine(1);
                
                printer.addTextAlign(printer.ALIGN_LEFT);
                printer.addText('----------------------------------------\n');
                printer.addText(`Unit: ${unitToConfig?.unitName || 'N/A'}\n`);
                printer.addText(`IP Address: ${drawerConfig.ip}\n`);
                printer.addText(`Port: ${drawerConfig.port}\n`);
                printer.addText(`Method: ePOS SDK\n`);
                printer.addText(`Status: Opening drawer...\n`);
                printer.addText('----------------------------------------\n');
                printer.addFeedLine(2);
                
                printer.addTextAlign(printer.ALIGN_CENTER);
                printer.addText('💰 Drawer should open now!\n');
                printer.addFeedLine(1);
                printer.addText('Test completed successfully\n');
                printer.addFeedLine(3);
                printer.addCut(printer.CUT_FEED);
                
                // Open the cash drawer
                printer.addPulse(printer.DRAWER_1, printer.PULSE_100);
                
                await new Promise((resolve, reject) => {
                    printer.send();
                    
                    printer.onreceive = (response) => {
                        if (response.success) {
                            console.log('Cash drawer test successful via ePOS SDK');
                            toast.success(`💰 Cash drawer test successful via ePOS SDK! Drawer opened on ${drawerConfig.ip}:${drawerConfig.port}`);
                            resolve(response);
                        } else {
                            reject(new Error(`Drawer test failed: ${response.code}`));
                        }
                    };
                    
                    printer.onerror = (error) => {
                        reject(new Error(`Drawer error: ${error.status}`));
                    };
                });
                
            } else {
                // Use proxy service for production or XML API for development (fallback)
                console.log('Testing cash drawer via XML API/Proxy');
                
                const isProduction = window.location.protocol === 'https:';
                
                // Enhanced cash drawer test with comprehensive receipt
                const drawerXml = `<?xml version="1.0" encoding="utf-8"?>
<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">
    <text align="center" width="2" height="2">DRAWER TEST</text>
    <feed line="1"/>
    <text align="center">Cash Drawer Test</text>
    <feed line="1"/>
    <text align="center">${new Date().toLocaleString()}</text>
    <feed line="1"/>
    <text>----------------------------------------</text>
    <feed line="1"/>
    <text>Unit: ${unitToConfig?.unitName || 'N/A'}</text>
    <feed line="1"/>
    <text>IP Address: ${drawerConfig.ip}</text>
    <feed line="1"/>
    <text>Port: ${drawerConfig.port}</text>
    <feed line="1"/>
    <text>Method: ${drawerConfig.method || 'ePOS-Print'}</text>
    <feed line="1"/>
    <text>SDK Available: ${isSDKAvailable ? 'Yes' : 'No'}</text>
    <feed line="1"/>
    <text>Environment: ${isProduction ? 'Production (Proxy)' : 'Development (Direct)'}</text>
    <feed line="1"/>
    <text>Connection: ${connectionMethod}</text>
    <feed line="1"/>
    <text>Status: Opening drawer...</text>
    <feed line="1"/>
    <text>----------------------------------------</text>
    <feed line="2"/>
    <text align="center">💰 Drawer should open now!</text>
    <feed line="1"/>
    <text align="center">Test completed successfully</text>
    <feed line="1"/>
    <text align="center">Check RJ11 cable connection</text>
    <feed line="2"/>
    <text align="center">Powered by Elevate Panel</text>
    <feed line="3"/>
    <cut type="feed"/>
    <pulse drawer="drawer_1" time="pulse_100"/>
</epos-print>`;

                if (isProduction) {
                    console.log('Using proxy service for drawer test...');
                    const result = await printerProxyService.print(
                        drawerConfig.ip, 
                        drawerConfig.port, 
                        drawerXml, 
                        drawerConfig.method || 'epos-print'
                    );
                    
                    if (result.success) {
                        console.log('Cash drawer test successful via proxy service');
                        toast.success(`💰 Cash drawer test successful via Proxy Service! Drawer opened on ${drawerConfig.ip}:${drawerConfig.port}`);
                    } else {
                        throw new Error(result.error || 'Drawer test failed via proxy');
                    }
                } else {
                    // Development: Direct HTTP connection
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 8000);
                    
                    const printUrl = drawerConfig.port === 8008 
                        ? `http://${drawerConfig.ip}:${drawerConfig.port}/epos/print`
                        : `http://${drawerConfig.ip}/epos/print`;
                    
                    await fetch(printUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'text/xml; charset=utf-8',
                            'SOAPAction': ''
                        },
                        body: drawerXml,
                        mode: 'no-cors',
                        signal: controller.signal
                    });

                    clearTimeout(timeoutId);
                    console.log('Cash drawer test successful via XML API for', `${drawerConfig.ip}:${drawerConfig.port}`);
                    toast.success(`💰 Cash drawer test successful via XML API! Drawer opened on ${drawerConfig.ip}:${drawerConfig.port}`);
                }
            }
            
        } catch (error) {
            console.error('Enhanced cash drawer test failed:', error);
            if (error.name === 'AbortError') {
                toast.error('⏱️ Connection test timed out - printer/drawer may be offline');
            } else if (error.name === 'TypeError') {
                toast.error(`🌐 Network error - Check if printer ${drawerConfig.ip} is accessible on your network`);
            } else {
                toast.error(`❌ Cash drawer test failed: ${error.message}. Check printer connection and RJ11 drawer cable.`);
            }
        } finally {
            setIsTestingDrawer(false);
        }
    };

    // Save configuration
    const handleSaveConfiguration = async () => {
        if (!unitToConfig) {
            toast.error('No unit selected for configuration');
            return;
        }

        setIsSavingConfig(true);

        try {
            // Generate configuration JSON (using existing database structure)
            const configurationData = {
                _id: unitToConfig._id,
                printerEnabled: printerEnabled,
                printerSetting: printerEnabled && selectedPrinter ? [{
                    ip: selectedPrinter.ip,
                    name: `Printer-${selectedPrinter.ip}`,
                    port: selectedPrinter.port || (selectedPrinter.method === 'epos-print' ? 8008 : 9100),
                    method: selectedPrinter.method,
                    autoCut: true,
                    connectionStatus: 'connected'
                }] : [],
                cashDrawerEnabled: cashDrawerEnabled,
                cashDrawerSettings: cashDrawerEnabled && selectedPrinter ? [{
                    ip: selectedPrinter.ip,
                    name: `Drawer-${selectedPrinter.ip}`,
                    port: selectedPrinter.port || (selectedPrinter.method === 'epos-print' ? 8008 : 9100),
                    autoOpenOnPrint: true,
                    connectionStatus: 'connected'
                }] : []
            };

            console.log('Configuration JSON to save:', JSON.stringify(configurationData, null, 2));

            // API call to update unit configuration
            const response = await HitApi(configurationData, updateUnit);

            if (response?.statusCode === 200) {
                toast.success('Configuration saved successfully!');
                
                // Update the local unit data
                const updatedUnits = ApiReducer.apiJson.map(unit => 
                    unit._id === unitToConfig._id 
                        ? { ...unit, ...configurationData }
                        : unit
                );
                dispatch(setApiJson(updatedUnits));
                
                handleCloseConfigModal();
                
                // Optionally refresh the unit list
                fetchUnit();
            } else {
                toast.error('Failed to save configuration. Please try again.');
            }

        } catch (error) {
            console.error('Configuration save error:', error);
            toast.error('Error occurred while saving configuration. Please try again.');
        } finally {
            setIsSavingConfig(false);
        }
    };

    const renderStatus = (value, row) => {
        const status = row.Status || row.status;

        if (status === undefined || status === null) return null;

        const isActive = status === "Active"
        const bgColor = isActive ? "bg-green-100" : "bg-red-100";
        const textColor = isActive ? "text-green-800" : "text-red-800";
        const label = isActive ? "Active" : "Inactive";

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
                {label}
            </span>
        );
    };

    const UnitCard = ({ unit, index }) => {
        const serialNumber = ((TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit) + index + 1;
        
        return (
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow duration-200">
                <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center">
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                            <span className="text-lg font-medium text-blue-600">
                                {unit.unitName && unit.unitName.charAt(0).toUpperCase()}
                            </span>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">{unit.unitName || 'N/A'}</h3>
                            <p className="text-sm text-gray-500">#{serialNumber}</p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        {renderStatus(null, unit)}
                    </div>
                </div>
                
                <div className="space-y-3">
                    <div>
                        <label className="text-sm font-medium text-gray-600">Unit Code</label>
                        <div className="mt-1">
                            <span className="px-3 py-1 bg-gray-100 rounded-md text-sm font-mono">
                                {unit.unitCode || 'N/A'}
                            </span>
                        </div>
                    </div>
                    
                    <div>
                        <label className="text-sm font-medium text-gray-600">Address</label>
                        <p className="text-sm text-gray-800 mt-1">{unit.address || 'N/A'}</p>
                    </div>
                    
                    <div className="space-y-3">
                        <div>
                            <label className="text-sm font-medium text-gray-600">GST Number</label>
                            <p className="text-sm text-gray-800 mt-1 font-mono break-all">{unit.gst || 'N/A'}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Email</label>
                            <p className="text-sm text-gray-800 mt-1 break-all">{unit.email || 'N/A'}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Phone</label>
                            <p className="text-sm text-gray-800 mt-1">{unit.phone || 'N/A'}</p>
                        </div>
                    </div>
                    
                    <div className="flex space-x-4">
                        <div>
                            <label className="text-sm font-medium text-gray-600">Printer</label>
                            <div className="flex items-center mt-1">
                                {unit.printerEnabled || (unit.printerSetting && unit.printerSetting.length > 0) ? (
                                    <span className="flex items-center text-sm text-green-600">
                                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        Configured
                                    </span>
                                ) : (
                                    <span className="flex items-center text-sm text-gray-400">
                                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                        Not configured
                                    </span>
                                )}
                            </div>
                        </div>
                        
                        <div>
                            <label className="text-sm font-medium text-gray-600">Cash Drawer</label>
                            <div className="flex items-center mt-1">
                                {unit.cashDrawerEnabled || (unit.cashDrawerSettings && unit.cashDrawerSettings.length > 0) ? (
                                    <span className="flex items-center text-sm text-green-600">
                                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        Configured
                                    </span>
                                ) : (
                                    <span className="flex items-center text-sm text-gray-400">
                                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                        Not configured
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="flex justify-end space-x-2 mt-6 pt-4 border-t border-gray-100">
                    <button
                        onClick={() => { setQrUnit(unit); setShowQrModal(true); }}
                        className="flex items-center px-3 py-2 text-sm font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 hover:border-purple-300 transition-colors duration-200"
                        title="Generate QR Code"
                    >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h7v7H3V3zm11 0h7v7h-7V3zm-11 11h7v7H3v-7zm14 3h.01M17 17h.01M21 17h.01M14 14h3v3h-3v-3zm3 3h3v3h-3v-3z" />
                        </svg>
                        QR Code
                    </button>
                    {(canEditUnit || canDeleteUnit) && (
                    <>
                        {canEditUnit && (
                            <>
                                <button
                                    onClick={() => handleConfig(unit)}
                                    className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:border-blue-300 transition-colors duration-200"
                                    title="Configure Unit"
                                >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                    </svg>
                                    Config
                                </button>
                                <EditButton
                                    onEdit={() => handleEdit(unit)}
                                    itemName="unit"
                                    tooltip="Edit Unit"
                                />
                            </>
                        )}
                        {canDeleteUnit && (
                            <DeleteButton
                                onDelete={() => handleDelete(unit)}
                                itemName="unit"
                                tooltip="Delete Unit"
                            />
                        )}
                    </>
                    )}
                </div>
            </div>
        );
    };

    // Show access denied message if user doesn't have read permission
    if (!canReadUnit) {
        return (
            <div className="p-5">
                <PageHeader
                    title={'Unit Management'}
                    description={'Manage organizational units and locations'}
                />
                <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                        <h3 className="text-lg font-medium text-red-600 mb-2">Access Denied</h3>
                        <p className="text-gray-600">You don't have permission to view units.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-5">
            <PageHeader
                title={'Unit Management'}
                description={'Manage organizational units and locations'}
            />
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                <div className="p-4 border-b border-gray-200">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold text-gray-900">Units</h2>
                        {canAddUnit && (
                            <button
                                onClick={() => navigate("/unit/add")}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200"
                            >
                                Add Unit
                            </button>
                        )}
                    </div>
                    <TableSearch
                        options={options}
                        onClick={handleSearch}
                        optionSelect={optionSelect}
                        setOptionSelect={setOptionSelect}
                        setValueSearched={setValueSearched}
                        valueSearched={valueSearched}
                        onClear={handleClearSearch}
                    />
                </div>
                
                <div className="p-4">
                    {isSearching ? (
                        <div className="flex justify-center items-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : Array.isArray(ApiReducer.apiJson) && ApiReducer.apiJson.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {ApiReducer.apiJson.map((unit, index) => (
                                <UnitCard key={unit._id || index} unit={unit} index={index} />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <div className="text-gray-400 text-6xl mb-4">📦</div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No units found</h3>
                            <p className="text-gray-500">Get started by adding your first unit.</p>
                        </div>
                    )}
                </div>
                
                {Array.isArray(ApiReducer.apiJson) && ApiReducer.apiJson.length > 0 && (
                    <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-700">
                                Showing {((TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit) + 1} to{' '}
                                {Math.min(TableDataReducer.pagination.page * TableDataReducer.pagination.limit, TableDataReducer.pagination.total)} of{' '}
                                {TableDataReducer.pagination.total} results
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => dispatch(setPagination({
                                        ...TableDataReducer.pagination,
                                        page: Math.max(1, TableDataReducer.pagination.page - 1)
                                    }))}
                                    disabled={TableDataReducer.pagination.page <= 1}
                                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <span className="px-3 py-1 text-sm bg-blue-50 border border-blue-200 rounded-md">
                                    {TableDataReducer.pagination.page}
                                </span>
                                <button
                                    onClick={() => dispatch(setPagination({
                                        ...TableDataReducer.pagination,
                                        page: TableDataReducer.pagination.page + 1
                                    }))}
                                    disabled={TableDataReducer.pagination.page >= Math.ceil(TableDataReducer.pagination.total / TableDataReducer.pagination.limit)}
                                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <DeleteConfirmationModal
                isOpen={showDeleteModal}
                onClose={handleCloseModal}
                onConfirm={confirmDelete}
                itemName={unitToDelete?.unitName}
                itemType="unit"
                isLoading={isDeleting}
            />

            {/* QR Code Modal */}
            {showQrModal && qrUnit && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl">
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="text-lg font-bold text-gray-900">Unit QR Code</h3>
                            <button
                                onClick={() => { setShowQrModal(false); setQrUnit(null); }}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="text-center">
                            <div className="inline-block p-4 bg-white rounded-xl border-2 border-gray-100 shadow-sm">
                                <img
                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qrUnit._id)}&format=png&margin=1`}
                                    alt="Unit QR Code"
                                    className="w-56 h-56"
                                    onError={(e) => {
                                        e.target.src = `https://chart.googleapis.com/chart?chs=250x250&cht=qr&chl=${encodeURIComponent(qrUnit._id)}&choe=UTF-8`;
                                    }}
                                />
                            </div>

                            <div className="mt-4 space-y-1">
                                <p className="text-base font-semibold text-gray-900">{qrUnit.unitName}</p>
                                <p className="text-sm text-gray-500 font-mono">{qrUnit.unitCode}</p>
                                <p className="text-xs text-gray-400 break-all">ID: {qrUnit._id}</p>
                            </div>

                            <div className="flex gap-3 mt-5">
                                <a
                                    href={`https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(qrUnit._id)}&format=png&margin=1`}
                                    download={`${qrUnit.unitName || 'unit'}-qr.png`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    Download
                                </a>
                                <button
                                    onClick={() => {
                                        const printWindow = window.open('', '_blank');
                                        printWindow.document.write(`
                                            <html><head><title>QR - ${qrUnit.unitName}</title>
                                            <style>body{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;margin:0;font-family:Arial,sans-serif;}
                                            img{width:300px;height:300px;}h2{margin:20px 0 4px;font-size:20px;}p{color:#666;font-size:14px;margin:2px 0;}</style></head>
                                            <body>
                                            <img src="https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(qrUnit._id)}&format=png&margin=1" />
                                            <h2>${qrUnit.unitName || ''}</h2>
                                            <p>${qrUnit.unitCode || ''}</p>
                                            </body></html>
                                        `);
                                        printWindow.document.close();
                                        printWindow.onload = () => { printWindow.print(); };
                                    }}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                    </svg>
                                    Print
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Configuration Modal */}
            {showConfigModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
                        <div className="mt-3">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Unit Configuration - {unitToConfig?.unitName}
                                </h3>
                                <button
                                    onClick={handleCloseConfigModal}
                                    disabled={isSavingConfig}
                                    className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                </button>
                            </div>
                            
                            <div className="space-y-6">
                                {/* Unit Info */}
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <h4 className="font-medium text-gray-900 mb-2">Unit Information</h4>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="text-gray-600">Unit Name:</span>
                                            <span className="ml-2 font-medium">{unitToConfig?.unitName}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Unit Code:</span>
                                            <span className="ml-2 font-medium">{unitToConfig?.unitCode}</span>
                                        </div>
                                        <div className="col-span-2">
                                            <span className="text-gray-600">Address:</span>
                                            <span className="ml-2 font-medium">{unitToConfig?.address}</span>
                                        </div>
                                        
                                        {/* Current Configuration Status */}
                                        <div>
                                            <span className="text-gray-600">Current Printer:</span>
                                            <div className="mt-1">
                                                {(unitToConfig?.printerSetting && unitToConfig?.printerSetting.length > 0) ? (
                                                    <span className="text-green-600 font-medium">
                                                        {unitToConfig.printerSetting[0].ip}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400">Not configured</span>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Current Drawer:</span>
                                            <div className="mt-1">
                                                {(unitToConfig?.cashDrawerSettings && unitToConfig?.cashDrawerSettings.length > 0) ? (
                                                    <span className="text-green-600 font-medium">
                                                        {unitToConfig.cashDrawerSettings[0].ip}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400">Not configured</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Current Printer Information - Compact */}
                                {(unitToConfig?.printerSetting && unitToConfig?.printerSetting.length > 0) && (
                                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <h4 className="font-medium text-blue-900 text-sm flex items-center">
                                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm12 4a1 1 0 011 1v6h-1a1 1 0 01-1-1v-4a1 1 0 00-1-1H7a1 1 0 00-1 1v4a1 1 0 01-1 1H4v-6a1 1 0 011-1h10z" clipRule="evenodd" />
                                                </svg>
                                                Current Printer
                                            </h4>
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={testPrinterConnection}
                                                    disabled={isTestingPrinter}
                                                    className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
                                                >
                                                    {isTestingPrinter ? (
                                                        <>
                                                            <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            <span>Testing...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                                                            </svg>
                                                            <span>Test</span>
                                                        </>
                                                    )}
                                                </button>
                                                <span className={`px-2 py-1 rounded-full text-xs flex items-center ${
                                                    isConnected 
                                                        ? 'text-green-600 bg-green-100' 
                                                        : 'text-yellow-600 bg-yellow-100'
                                                }`}>
                                                    <div className={`w-2 h-2 rounded-full mr-1 ${
                                                        isConnected ? 'bg-green-400' : 'bg-yellow-400'
                                                    }`}></div>
                                                    {isConnected ? `Connected (${connectionMethod})` : 'Configured'}
                                                </span>
                                                {isSDKAvailable && (
                                                    <span className="text-blue-600 bg-blue-100 px-2 py-1 rounded-full text-xs">
                                                        SDK
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-2 text-xs">
                                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded font-mono">
                                                {unitToConfig.printerSetting[0].ip}:{unitToConfig.printerSetting[0].port}
                                            </span>
                                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded capitalize">
                                                {unitToConfig.printerSetting[0].method || 'epos-print'}
                                            </span>
                                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                                Auto-cut: {unitToConfig.printerSetting[0].autoCut !== false ? 'On' : 'Off'}
                                            </span>
                                            {lastConnectionTest && (
                                                <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                                    Last test: {new Date(lastConnectionTest).toLocaleTimeString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Current Cash Drawer Information - Compact */}
                                {(unitToConfig?.cashDrawerSettings && unitToConfig?.cashDrawerSettings.length > 0) && (
                                    <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <h4 className="font-medium text-green-900 text-sm flex items-center">
                                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                                </svg>
                                                Current Cash Drawer
                                            </h4>
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={testDrawerConnection}
                                                    disabled={isTestingDrawer}
                                                    className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
                                                >
                                                    {isTestingDrawer ? (
                                                        <>
                                                            <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            <span>Testing...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                                                            </svg>
                                                            <span>Test</span>
                                                        </>
                                                    )}
                                                </button>
                                                <span className={`px-2 py-1 rounded-full text-xs flex items-center ${
                                                    isConnected 
                                                        ? 'text-green-600 bg-green-100' 
                                                        : 'text-yellow-600 bg-yellow-100'
                                                }`}>
                                                    <div className={`w-2 h-2 rounded-full mr-1 ${
                                                        isConnected ? 'bg-green-400' : 'bg-yellow-400'
                                                    }`}></div>
                                                    {isConnected ? `Connected (${connectionMethod})` : 'Configured'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-2 text-xs">
                                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded font-mono">
                                                {unitToConfig.cashDrawerSettings[0].ip}:{unitToConfig.cashDrawerSettings[0].port}
                                            </span>
                                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                                                Auto-open: {unitToConfig.cashDrawerSettings[0].autoOpenOnPrint !== false ? 'On' : 'Off'}
                                            </span>
                                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                                                Connection: {connectionMethod || 'XML API'}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Reconfigure Section */}
                                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                                    <h4 className="font-medium text-yellow-900 mb-2 flex items-center">
                                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                                        </svg>
                                        Reconfigure Devices
                                    </h4>
                                    <div className="text-yellow-700 text-sm mb-4">
                                        <p>Use the controls below to modify or update your printer and cash drawer configuration.</p>
                                        
                                        {/* Print Quality Settings */}
                                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                            <h5 className="font-medium text-blue-900 mb-2 flex items-center">
                                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                                Print Quality Settings
                                            </h5>
                                            <div className="grid grid-cols-2 gap-2 text-xs">
                                                <div className="flex items-center justify-between">
                                                    <span>Density:</span>
                                                    <select 
                                                        value={printQualitySettings.density}
                                                        onChange={(e) => setPrintQualitySettings(prev => ({...prev, density: e.target.value}))}
                                                        className="ml-2 px-1 py-0.5 border border-blue-300 rounded text-xs"
                                                    >
                                                        <option value="low">Low</option>
                                                        <option value="medium">Medium</option>
                                                        <option value="high">High</option>
                                                        <option value="ultra">Ultra</option>
                                                    </select>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span>Speed:</span>
                                                    <select 
                                                        value={printQualitySettings.speed}
                                                        onChange={(e) => setPrintQualitySettings(prev => ({...prev, speed: e.target.value}))}
                                                        className="ml-2 px-1 py-0.5 border border-blue-300 rounded text-xs"
                                                    >
                                                        <option value="slow">Slow (Best)</option>
                                                        <option value="medium">Medium</option>
                                                        <option value="fast">Fast</option>
                                                    </select>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span>Smoothing:</span>
                                                    <label className="flex items-center">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={printQualitySettings.smoothing}
                                                            onChange={(e) => setPrintQualitySettings(prev => ({...prev, smoothing: e.target.checked}))}
                                                            className="ml-2 w-3 h-3"
                                                        />
                                                    </label>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span>Double Strike:</span>
                                                    <label className="flex items-center">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={printQualitySettings.doubleStrike}
                                                            onChange={(e) => setPrintQualitySettings(prev => ({...prev, doubleStrike: e.target.checked}))}
                                                            className="ml-2 w-3 h-3"
                                                        />
                                                    </label>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span>Enhanced Graphics:</span>
                                                    <label className="flex items-center">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={printQualitySettings.enhancedGraphics}
                                                            onChange={(e) => setPrintQualitySettings(prev => ({...prev, enhancedGraphics: e.target.checked}))}
                                                            className="ml-2 w-3 h-3"
                                                        />
                                                    </label>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span>Paper Saving:</span>
                                                    <label className="flex items-center">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={printQualitySettings.paperSaving}
                                                            onChange={(e) => setPrintQualitySettings(prev => ({...prev, paperSaving: e.target.checked}))}
                                                            className="ml-2 w-3 h-3"
                                                        />
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="mt-2 flex space-x-2">
                                                <button
                                                    onClick={() => setPrintQualitySettings({
                                                        density: 'ultra', speed: 'slow', smoothing: true, 
                                                        doubleStrike: true, enhancedGraphics: true, 
                                                        paperSaving: false, fontEnhancement: true
                                                    })}
                                                    className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                                                >
                                                    Best Quality
                                                </button>
                                                <button
                                                    onClick={() => setPrintQualitySettings({
                                                        density: 'medium', speed: 'fast', smoothing: false, 
                                                        doubleStrike: false, enhancedGraphics: false, 
                                                        paperSaving: true, fontEnhancement: false
                                                    })}
                                                    className="px-2 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700 transition-colors"
                                                >
                                                    Fast Print
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {isDetecting && (
                                            <div className="mt-2 flex items-center space-x-2">
                                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <span>Scanning network for printers... {detectionProgress}%</span>
                                            </div>
                                        )}
                                        {availablePrinters.length > 0 && (
                                            <div className="mt-2">
                                                <span className="text-sm font-medium">Found {availablePrinters.length} printer(s) on network</span>
                                            </div>
                                        )}
                                    </div>
                                
                                    {/* Printer Configuration */}
                                    <PrinterConfiguration
                                        printerEnabled={printerEnabled}
                                        setPrinterEnabled={setPrinterEnabled}
                                        setCashDrawerEnabled={setCashDrawerEnabled}
                                        onPrinterDataChange={handlePrinterDataChange}
                                    />
                                </div>

                                {/* Cash Drawer Control */}
                                <CashDrawerControl
                                    printerEnabled={printerEnabled}
                                    cashDrawerEnabled={cashDrawerEnabled}
                                    setCashDrawerEnabled={handleCashDrawerToggle}
                                    selectedPrinter={selectedPrinter}
                                    cashBalance={cashBalance}
                                    isLoadingCashBalance={isLoadingCashBalance}
                                    isCreatingCashBalance={isCreatingCashBalance}
                                />
                            </div>

                            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                                <button
                                    onClick={handleCloseConfigModal}
                                    disabled={isSavingConfig}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveConfiguration}
                                    disabled={isSavingConfig}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                                >
                                    {isSavingConfig && (
                                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    )}
                                    <span>{isSavingConfig ? 'Saving...' : 'Save Configuration'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UnitMaster;